/**
 * SkillVersionEditForm.tsx — edit Mari's prompt copy from a base version and
 * publish it (story E0-BO-CFG-SKILL-REGISTRY-ADMIN).
 *
 * Loads the base version (useSkillVersionDetail) and lets the operator edit
 * ONLY the prompt copy: `grounding[*]` (instruction / anchor_template /
 * reference_facts / per-fact `fact_categories`), `product_facts`, and each
 * classifier's `system_prompt`. `graph_config_json` is rendered read-only for
 * orientation and is never part of the submitted body — the server copies it
 * from the base version and ignores anything sent here, so this form
 * structurally cannot send it.
 *
 * `fact_categories` / `mandatory_fact_patterns` live on EACH grounding entry
 * (keyed by a `reference_facts` key WITHIN that entry) — verified against
 * `packages/shared/models/skill_config.py::GroundingEntry` and
 * `services/capital-service/app/domain/skill_registry/validation.py` on
 * PR #534. They are NOT top-level, and they describe that entry's own facts,
 * not `product_facts` (a plain key/value map with no category metadata).
 *
 * Every grounding entry is rebuilt by SPREADING the base entry and overriding
 * only the fields deliberately edited here — never by reconstructing from a
 * named field list. A reconstruct-from-fields approach silently drops any
 * field on GroundingEntry this form doesn't know about (today that would be
 * `mandatory_string_categories` on an untouched intent, or a future field);
 * a save through this panel must never be lossy.
 *
 * Save and activate are two DELIBERATE, separate actions (never one control):
 *   1. "Create version" → POST .../versions. Success leaves the version
 *      INACTIVE — nothing has changed for any conversation yet. A 422 maps
 *      each `{field_path, reason}` to its field; anything unmatched is never
 *      dropped, it renders in a fallback list. Non-blocking `warnings`
 *      (`{field_path, reason}[]` — the SAME shape as a 422 error, e.g. a
 *      regex-looking `mandatory_fact_patterns` alternate) render in an
 *      unmissable banner, not a toast.
 *   2. "Activate this version" (enabled only after a successful create) → a
 *      review step states plainly what activation does (new conversations
 *      pick it up immediately; conversations already in progress keep their
 *      original prompt permanently) before the operator confirms.
 *
 * --- Redesign (2026-08-04, `Product/Design/20260804_GARRETT_mari-config-
 * editor-redesign.md`) ---
 * 18 grounding intents are now `<details>` behind a filterable left rail,
 * collapsed by default and force-opened on a field error; the raw reference-
 * facts JSON is now a per-row table (Advanced JSON stays as an alternate view
 * over the SAME `referenceFactsJson` state, not a second source of truth);
 * every Instruction opens with a detected, locked "shared preamble" strip
 * (see `instructionSplit.ts`) so only the part that differs is edited inline;
 * Instruction / anchor template / classifier prompts get a `<dialog>`-based
 * focused editor; graph topology is closed-by-default with a plain node list;
 * Create/Activate live in a sticky bottom bar, with the activation review
 * step now a `<dialog>`. None of the above changes what is save-able or
 * sendable — every load-bearing rule in the header above is unchanged.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import {
  FACT_CATEGORIES,
  type ClassifierPromptConfig,
  type CreateSkillVersionResponse,
  type FactCategory,
  type GroundingEntry,
  type SkillVersionFieldError,
} from '../../api/types';
import { useSkillVersionDetail, type SkillVersionDetailParams } from './useSkillVersionDetail';
import { detectSharedPreamble, joinInstruction, splitInstruction } from './instructionSplit';
import { ActivateVersionControl } from './ActivateVersionControl';
import { AgenticConfigEditor } from './AgenticConfigEditor';
import { useIsAdmin } from '../../auth/useIsAdmin';
import { Field, inputClass } from '../../components/Field';
import floating from '../../lib/floating';

export interface SkillVersionEditFormProps {
  apiClient: ApiClient;
  base: SkillVersionDetailParams;
  /** Called after a successful activation, so the caller can refresh the list. */
  onActivated?: () => void;
}

type CreatePhase = 'idle' | 'submitting' | 'created' | 'error';

interface GroundingDraft {
  instruction: string;
  anchorTemplate: string;
  /** Raw JSON text for reference_facts — parsed on submit. Also the single
   * source of truth behind the per-row fact table (§5 of the redesign spec):
   * a row edit parses this, patches one key, and reserializes back into it. */
  referenceFactsJson: string;
  /** Set of fact keys to render a row/selector for. Fixed at seed time, and
   * re-derived from `referenceFactsJson` whenever the Advanced JSON view is
   * closed (so adding/removing a key there is reflected in the row table). */
  factKeys: string[];
  /** Category override per fact key; '' = unset/omit from the saved map. */
  factCategories: Record<string, FactCategory | ''>;
}

const groundingToDraft = (entry: GroundingEntry): GroundingDraft => {
  // Pre-migration-0075 rows have neither key at all (see GroundingEntry in
  // api/types.ts) — `?? {}` reads as "no categories yet", never written back
  // unless the operator sets one (handleCreate only adds keys it touches).
  const factCategories = entry.fact_categories ?? {};
  const factKeys = [
    ...new Set([...Object.keys(entry.reference_facts), ...Object.keys(factCategories)]),
  ];
  return {
    instruction: entry.instruction,
    anchorTemplate: entry.anchor_template,
    referenceFactsJson: JSON.stringify(entry.reference_facts, null, 2),
    factKeys,
    factCategories: Object.fromEntries(factKeys.map((key) => [key, factCategories[key] ?? ''])),
  };
};
const parseReferenceFacts = (json: string): Record<string, string | string[]> => {
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed != null && typeof parsed === 'object'
      ? (parsed as Record<string, string | string[]>)
      : {};
  } catch {
    return {};
  }
};

/** Re-derives factKeys/factCategories from the LIVE referenceFactsJson —
 * same derivation as groundingToDraft, just run again against the current
 * draft instead of only at seed time (used when Advanced JSON collapses). */
const rederiveFactRows = (draft: GroundingDraft): GroundingDraft => {
  const parsed = parseReferenceFacts(draft.referenceFactsJson);
  const factKeys = [...new Set([...Object.keys(parsed), ...Object.keys(draft.factCategories)])];
  return {
    ...draft,
    factKeys,
    factCategories: Object.fromEntries(
      factKeys.map((key) => [key, draft.factCategories[key] ?? '']),
    ),
  };
};

/**
 * A textarea that autogrows by line count (pure function of `value`, capped
 * at 12 rows — no scrollHeight measurement, so it never steals focus or
 * scroll position, per the redesign spec's accessibility note) plus an
 * "expand" affordance opening a native `<dialog>` focused editor.
 *
 * The dialog edits a local BUFFER, snapshotted from `value` on open — not
 * the live draft directly. Salvar commits the buffer via `onChange`; Cancelar
 * (and Esc / any other native dismissal, via `onClose`) discards it. A
 * control labelled "Cancelar" that silently kept edits (the original
 * same-state design in the redesign spec's §3) is a data-loss-adjacent bug,
 * not a simplification — see PR #573 review, Fowler thread 2452.
 */
const ExpandableTextarea = ({
  id,
  testId,
  value,
  onChange,
  disabled = undefined,
  minRows = 4,
}: {
  id: string;
  testId: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  minRows?: number;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(value);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dialogTextareaRef = useRef<HTMLTextAreaElement>(null);
  const rows = Math.min(12, Math.max(minRows, value.split('\n').length));

  const openDialog = () => {
    setDraft(value); // snapshot — the dialog edits this, not the live draft
    setExpanded(true);
  };

  useEffect(() => {
    if (expanded && dialogRef.current != null && !dialogRef.current.open) {
      dialogRef.current.showModal();
      dialogTextareaRef.current?.focus();
    }
  }, [expanded]);

  return (
    <div className="flex flex-col gap-1">
      <textarea
        id={id}
        data-testid={testId}
        rows={rows}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={`${inputClass} w-full`}
      />
      <button
        type="button"
        data-testid={`${testId}-expand`}
        onClick={openDialog}
        className="self-end max-md:min-h-6 text-meta text-gold hover:underline"
      >
        ⤢ {t('config.edit.expandField')}
      </button>
      {expanded ? (
        <dialog
          ref={dialogRef}
          data-testid={`${testId}-dialog`}
          // Esc / backdrop dismissal — the discard path, same as Cancelar.
          onClose={() => setExpanded(false)}
          className="w-full max-w-3xl rounded-lg bg-surface-1 p-4 text-ink-1 ring-1 ring-inset ring-ink-3/20 backdrop:bg-canvas/70"
        >
          <div className="flex flex-col gap-2">
            <textarea
              ref={dialogTextareaRef}
              data-testid={`${testId}-dialog-textarea`}
              rows={20}
              value={draft}
              disabled={disabled}
              onChange={(event) => setDraft(event.target.value)}
              className={`${inputClass} w-full font-mono`}
            />
            <p className="text-meta text-ink-3">{draft.length}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid={`${testId}-dialog-save`}
                onClick={() => {
                  onChange(draft);
                  dialogRef.current?.close();
                }}
                className="rounded-lg bg-gold max-md:min-h-11 px-3 py-1.5 text-meta font-medium text-on-gold hover:bg-gold/90"
              >
                {t('config.edit.dialogSave')}
              </button>
              <button
                type="button"
                data-testid={`${testId}-dialog-cancel`}
                onClick={() => dialogRef.current?.close()}
                className="rounded-lg bg-surface-2 px-3 py-1.5 text-meta font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3"
              >
                {t('config.edit.dialogCancel')}
              </button>
            </div>
          </div>
        </dialog>
      ) : null}
    </div>
  );
};

export const SkillVersionEditForm = ({
  apiClient,
  base,
  onActivated = undefined,
}: SkillVersionEditFormProps) => {
  const { t } = useTranslation();
  const { detail, phase } = useSkillVersionDetail(apiClient, base);
  const isAdmin = useIsAdmin();

  const [newConfigVersion, setNewConfigVersion] = useState('');
  const [grounding, setGrounding] = useState<Record<string, GroundingDraft>>({});
  const [productFacts, setProductFacts] = useState<Record<string, string>>({});
  const [classifiers, setClassifiers] = useState<Record<string, ClassifierPromptConfig>>({});
  const seeded = useRef(false);
  // The UNTOUCHED base grounding entries — every save spreads from here so any
  // field this form does not deliberately edit (mandatory_string_categories,
  // mandatory_fact_patterns, or a future field) is preserved, never dropped.
  const baseGroundingRef = useRef<Record<string, GroundingEntry>>({});
  // Detected once at seed time (§4) — never recomputed per render, never
  // hardcoded to today's known preamble text.
  const sharedPreambleRef = useRef<string>('');

  const [referenceFactsErrors, setReferenceFactsErrors] = useState<Record<string, string>>({});
  const [createPhase, setCreatePhase] = useState<CreatePhase>('idle');
  const [fieldErrors, setFieldErrors] = useState<SkillVersionFieldError[]>([]);
  const [createError, setCreateError] = useState<'unavailable' | 'unauthorized' | null>(null);
  const [created, setCreated] = useState<CreateSkillVersionResponse | null>(null);

  // Intent-index rail state (§1/§2 of the redesign spec).
  const [filterText, setFilterText] = useState('');
  const [openIntents, setOpenIntents] = useState<Set<string>>(new Set());
  // Escape hatch (§4): intents where the operator chose to edit the shared
  // preamble too, switching that one field back to full-text, unsplit.
  const [forceFullInstruction, setForceFullInstruction] = useState<Set<string>>(new Set());
  const detailsRefs = useRef<Record<string, HTMLDetailsElement | null>>({});

  // Seed the editable drafts once, from the base version — never re-seed on a
  // later refetch (there is none: useSkillVersionDetail fetches once per
  // stable `base`), so an in-progress edit is never clobbered.
  useEffect(() => {
    if (detail == null || seeded.current) return;
    seeded.current = true;
    const groundingSource = detail.prompt_template_json.grounding ?? {};
    baseGroundingRef.current = groundingSource;
    sharedPreambleRef.current = detectSharedPreamble(
      Object.values(groundingSource).map((entry) => entry.instruction),
    );
    setGrounding(
      Object.fromEntries(
        Object.entries(groundingSource).map(([intent, entry]) => [intent, groundingToDraft(entry)]),
      ),
    );
    setProductFacts({ ...(detail.prompt_template_json.product_facts ?? {}) });
    setClassifiers({ ...(detail.prompt_template_json.classifiers ?? {}) });
  }, [detail]);

  // An intent with a field error must render OPEN, not just badged (a11y —
  // collapsed <details> content is invisible to a screen reader).
  useEffect(() => {
    if (fieldErrors.length === 0) return;
    const affected = new Set<string>();
    fieldErrors.forEach((error) => {
      const [section, intent] = error.field_path.split('.');
      if (section === 'grounding' && intent != null) affected.add(intent);
    });
    if (affected.size === 0) return;
    setOpenIntents((prev) => new Set([...prev, ...affected]));
  }, [fieldErrors]);

  if (phase === 'unauthorized') {
    return (
      <p role="alert" data-testid="config-edit-unauthorized" className="text-body text-status-down">
        {t('config.errors.unauthorized')}
      </p>
    );
  }
  if (phase === 'notFound') {
    return (
      <p role="alert" data-testid="config-edit-notfound" className="text-body text-status-down">
        {t('config.edit.notFound')}
      </p>
    );
  }
  if (phase === 'loading' || detail == null) {
    return (
      <p role="status" data-testid="config-edit-loading" className="text-body text-ink-2">
        {t('config.edit.loading')}
      </p>
    );
  }
  if (phase === 'error') {
    return (
      <p role="alert" data-testid="config-edit-error" className="text-body text-status-down">
        {t('config.edit.error')}
      </p>
    );
  }

  // The single switch (ADR-084 D6 design doc §Tab visibility): a row that
  // carries agent_config_json is agentic-shaped by the time this panel sees
  // it (D1a normalises on read) — never checked alongside prompt_template_json.
  const isAgentic = detail.agent_config_json != null;

  const errorFor = (path: string): string | undefined =>
    fieldErrors.find((error) => error.field_path === path)?.reason;
  const consumedPaths = new Set<string>();
  const trackedErrorFor = (path: string): string | undefined => {
    const reason = errorFor(path);
    if (reason != null) consumedPaths.add(path);
    return reason;
  };
  // NOTE: computed via an IIFE at its render position below, not here — every
  // `trackedErrorFor` call that consumes a path happens inside the JSX tree
  // rendered ABOVE that position (grounding/product_facts/classifiers), and
  // JSX children evaluate left-to-right in one render pass. Computing this
  // eagerly, before that JSX evaluates, would see `consumedPaths` still
  // empty and wrongly flag every matched error as unmatched too.

  // Graph topology orientation (§6) — counts + a plain node-id list, derived
  // client-side from whatever shape graph_config_json actually has (a bare
  // string array in older fixtures, GraphConfig.nodes[*].name in production).
  const graphNodes = detail.graph_config_json.nodes;
  let nodeList: string[] = [];
  if (Array.isArray(graphNodes)) {
    nodeList = graphNodes.map((node) =>
      node != null && typeof node === 'object' && 'name' in node
        ? String((node as { name: unknown }).name)
        : String(node),
    );
  } else if (graphNodes != null && typeof graphNodes === 'object') {
    nodeList = Object.keys(graphNodes);
  }
  const graphEdges = detail.graph_config_json.edges;
  let edgeCount = 0;
  if (Array.isArray(graphEdges)) {
    edgeCount = graphEdges.length;
  } else if (graphEdges != null && typeof graphEdges === 'object') {
    edgeCount = Object.keys(graphEdges).length;
  }

  const intents = Object.keys(grounding);
  const hasGroundingError = (intent: string) =>
    fieldErrors.some((error) => error.field_path.startsWith(`grounding.${intent}.`));
  const railIntents = intents.filter(
    (intent) =>
      intent.toLowerCase().includes(filterText.trim().toLowerCase()) || hasGroundingError(intent),
  );

  const openIntent = (intent: string) => {
    setOpenIntents((prev) => new Set(prev).add(intent));
    detailsRefs.current[intent]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const setFactValue = (intent: string, key: string, value: string | string[]) => {
    setGrounding((prev) => {
      const draft = prev[intent]!;
      const parsed = parseReferenceFacts(draft.referenceFactsJson);
      parsed[key] = value;
      return {
        ...prev,
        [intent]: { ...draft, referenceFactsJson: JSON.stringify(parsed, null, 2) },
      };
    });
  };

  const handleCreate = async () => {
    // Parse every grounding reference_facts textarea; block submit locally on
    // malformed JSON rather than sending an invalid body.
    const parsedFacts: Record<string, Record<string, string | string[]>> = {};
    const parseErrors: Record<string, string> = {};
    Object.entries(grounding).forEach(([intent, draft]) => {
      try {
        parsedFacts[intent] = JSON.parse(draft.referenceFactsJson) as Record<
          string,
          string | string[]
        >;
      } catch {
        parseErrors[intent] = t('config.edit.grounding.referenceFactsInvalidJson');
      }
    });
    setReferenceFactsErrors(parseErrors);
    if (Object.keys(parseErrors).length > 0) return;

    // Rebuild each entry by SPREADING the untouched base entry — this is the
    // load-bearing part: any field on GroundingEntry this form does not
    // deliberately override (mandatory_string_categories,
    // mandatory_fact_patterns, or one added later) survives unchanged.
    const nextGrounding: Record<string, GroundingEntry> = Object.fromEntries(
      Object.entries(grounding).map(([intent, draft]) => {
        const original = baseGroundingRef.current[intent]!;
        const nextFactCategories: Record<string, FactCategory> = { ...original.fact_categories };
        Object.entries(draft.factCategories).forEach(([key, value]) => {
          if (value === '') delete nextFactCategories[key];
          else nextFactCategories[key] = value;
        });
        const nextEntry: GroundingEntry = {
          ...original,
          instruction: draft.instruction,
          anchor_template: draft.anchorTemplate,
          reference_facts: parsedFacts[intent] ?? {},
        };
        // Absent must stay absent: a pre-migration-0075 entry with no
        // `fact_categories` key must not gain an empty `{}` one just because
        // the operator never touched a category selector.
        if (original.fact_categories != null || Object.keys(nextFactCategories).length > 0) {
          nextEntry.fact_categories = nextFactCategories;
        }
        return [intent, nextEntry];
      }),
    );

    setCreatePhase('submitting');
    setCreateError(null);
    setFieldErrors([]);
    try {
      const response = await apiClient.createSkillVersion(base.skillName, {
        product_code: base.productCode,
        base_config_version: base.configVersion,
        new_config_version: newConfigVersion.trim(),
        prompt_template_json: {
          ...detail.prompt_template_json,
          grounding: nextGrounding,
          product_facts: productFacts,
          classifiers,
        },
      });
      setCreated(response);
      setCreatePhase('created');
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        const { body } = error;
        let list: unknown[] = [];
        if (Array.isArray(body)) {
          list = body;
        } else if (
          body != null &&
          typeof body === 'object' &&
          Array.isArray((body as { detail?: unknown }).detail)
        ) {
          list = (body as { detail: unknown[] }).detail;
        }
        setFieldErrors(
          list.filter(
            (item): item is SkillVersionFieldError =>
              item != null &&
              typeof item === 'object' &&
              typeof (item as { field_path?: unknown }).field_path === 'string' &&
              typeof (item as { reason?: unknown }).reason === 'string',
          ),
        );
        setCreatePhase('error');
      } else if (error instanceof UnauthorizedError) {
        setCreateError('unauthorized');
        setCreatePhase('error');
      } else {
        setCreateError('unavailable');
        setCreatePhase('error');
      }
    }
  };

  return (
    <section aria-labelledby="config-edit-heading" className="flex flex-col gap-6">
      <h2 id="config-edit-heading" className="text-h2 text-ink-1">
        {t('config.edit.heading', { skill: base.skillName, base: base.configVersion })}
      </h2>

      <div className="flex flex-col gap-1">
        <label htmlFor="new-config-version" className="text-meta font-medium text-ink-2">
          {t('config.edit.newVersion')}
        </label>
        <input
          id="new-config-version"
          data-testid="config-new-version"
          type="text"
          value={newConfigVersion}
          disabled={createPhase === 'submitting'}
          onChange={(event) => setNewConfigVersion(event.target.value)}
          className={`${inputClass} max-w-xs`}
        />
      </div>

      {/* Moved near the top (§7) — the operator must see whether the create
          succeeded, and any warnings, without scrolling down first. */}
      {createPhase === 'created' && created != null ? (
        <div
          data-testid="config-created-banner"
          className="flex flex-col gap-2 rounded-lg bg-surface-2 p-4 ring-1 ring-inset ring-ink-3/10"
        >
          <p className="text-body text-ink-1">
            {t('config.edit.created', { version: created.config_version })}
          </p>
          {created.warnings.length > 0 ? (
            <div
              id="config-create-warnings"
              role="alert"
              data-testid="config-create-warnings"
              className="min-w-0 break-words [overflow-wrap:anywhere] rounded-lg bg-warning/15 p-3 text-meta text-warning ring-1 ring-inset ring-warning/40"
            >
              <p className="font-medium">{t('config.edit.warningsHeading')}</p>
              <ul className="list-inside list-disc">
                {created.warnings.map((warning) => (
                  <li key={`${warning.field_path}:${warning.reason}`}>
                    {warning.field_path}: {warning.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {createError != null ? (
        <p
          role="alert"
          data-testid={`config-create-error-${createError}`}
          className="rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40"
        >
          {t(`config.edit.errors.${createError}`)}
        </p>
      ) : null}

      {/* graph_config_json — READ-ONLY orientation only. There is no input
          bound to it and it is never included in the create request body:
          the topology is structurally not editable or sendable from here. */}
      <details
        data-testid="config-edit-topology-details"
        className="rounded-lg bg-surface-2 ring-1 ring-inset ring-ink-3/10"
      >
        <summary className="cursor-pointer px-4 py-3 text-meta font-medium uppercase tracking-wide text-ink-3">
          <span className="block">{t('config.edit.topology.heading')}</span>
          <span className="block normal-case text-body text-ink-2">
            {t('config.edit.topology.summary', { nodes: nodeList.length, edges: edgeCount })}
          </span>
        </summary>
        <div className="flex flex-col gap-2 px-4 pb-4">
          <p className="text-meta text-ink-3">{t('config.edit.topology.hint')}</p>
          <ul
            data-testid="config-edit-graph-nodes"
            className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-meta text-ink-2"
          >
            {nodeList.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
          <pre
            data-testid="config-edit-graph-readonly"
            aria-readonly="true"
            className="max-h-[70vh] overflow-auto rounded-lg bg-surface-1 p-3 font-mono text-meta text-ink-2 ring-1 ring-inset ring-ink-3/20"
          >
            {JSON.stringify(detail.graph_config_json, null, 2)}
          </pre>
        </div>
      </details>

      {/* agent_config_json — READ-ONLY raw-JSON orientation for a
          NON-agentic (legacy) row only (ADR-082 D-6). For an agentic row
          (isAgentic) the tab strip below edits this document directly
          (ADR-084 D6), so the raw dump is superseded, not duplicated. */}
      {!isAgentic ? (
        <details
          data-testid="config-edit-agent-details"
          className="rounded-lg bg-surface-2 ring-1 ring-inset ring-ink-3/10"
        >
          <summary className="cursor-pointer px-4 py-3 text-meta font-medium uppercase tracking-wide text-ink-3">
            <span className="block">{t('config.edit.agentConfig.heading')}</span>
            <span className="block normal-case text-body text-ink-2">
              {t('config.edit.agentConfig.none')}
            </span>
          </summary>
        </details>
      ) : null}

      {isAgentic ? (
        <AgenticConfigEditor
          apiClient={apiClient}
          base={base}
          detail={detail}
          isAdmin={isAdmin}
          newConfigVersion={newConfigVersion}
          createDisabled={createPhase === 'submitting'}
          onCreateStart={() => {
            setCreatePhase('submitting');
            setCreateError(null);
          }}
          onCreated={(response) => {
            setCreated(response);
            setCreatePhase('created');
          }}
          onCreateError={(kind) => {
            setCreateError(kind);
            setCreatePhase('error');
          }}
          created={created}
          onActivated={onActivated}
        />
      ) : (
        <>
          <section aria-labelledby="config-grounding-heading" className="flex flex-col gap-4">
            <h3 id="config-grounding-heading" className="text-h3 text-ink-1">
              {t('config.edit.grounding.heading')}
            </h3>
            <p className="text-meta text-ink-3">{t('config.edit.grounding.headingHint')}</p>

            {intents.length === 0 ? (
              <p data-testid="config-grounding-empty" className="text-body text-ink-2">
                {t('config.edit.grounding.empty')}
              </p>
            ) : (
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                <aside className="flex flex-col gap-2 lg:sticky lg:top-4 lg:w-64 lg:flex-shrink-0">
                  <label htmlFor="grounding-intent-filter" className="sr-only">
                    {t('config.edit.grounding.filterLabel')}
                  </label>
                  <input
                    id="grounding-intent-filter"
                    data-testid="grounding-intent-filter"
                    type="text"
                    value={filterText}
                    onChange={(event) => setFilterText(event.target.value)}
                    placeholder={t('config.edit.grounding.filterPlaceholder')}
                    className={inputClass}
                  />
                  <div className="flex gap-3">
                    <button
                      type="button"
                      data-testid="grounding-expand-all"
                      onClick={() => setOpenIntents(new Set(intents))}
                      className="max-md:min-h-6 text-meta text-gold hover:underline"
                    >
                      {t('config.edit.grounding.expandAll')}
                    </button>
                    <button
                      type="button"
                      data-testid="grounding-collapse-all"
                      onClick={() => setOpenIntents(new Set())}
                      className="max-md:min-h-6 text-meta text-gold hover:underline"
                    >
                      {t('config.edit.grounding.collapseAll')}
                    </button>
                  </div>
                  <nav
                    aria-label={t('config.edit.grounding.heading')}
                    className="flex max-h-[60vh] flex-col gap-1 overflow-auto lg:max-h-[calc(100vh-12rem)]"
                  >
                    {railIntents.map((intent) => {
                      const isOpen = openIntents.has(intent);
                      return (
                        <button
                          key={intent}
                          type="button"
                          data-testid={`grounding-rail-${intent}`}
                          onClick={() => openIntent(intent)}
                          className={`flex items-center gap-2 rounded px-2 py-1 text-left text-meta ${
                            isOpen ? 'bg-surface-1 text-ink-1' : 'text-ink-2 hover:bg-surface-1'
                          }`}
                        >
                          <span
                            aria-hidden="true"
                            className={`h-1.5 w-1.5 rounded-full ${isOpen ? 'bg-gold' : 'bg-ink-3/40'}`}
                          />
                          <span className="flex-1">{intent}</span>
                          {hasGroundingError(intent) ? (
                            <span aria-hidden="true" className="text-status-down">
                              ⚠
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </nav>
                </aside>

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  {Object.entries(grounding).map(([intent, draft]) => {
                    const original = baseGroundingRef.current[intent];
                    const preamble = forceFullInstruction.has(intent)
                      ? ''
                      : sharedPreambleRef.current;
                    const split = splitInstruction(draft.instruction, preamble);
                    const instructionError = trackedErrorFor(`grounding.${intent}.instruction`);
                    const anchorError = trackedErrorFor(`grounding.${intent}.anchor_template`);
                    const factsError =
                      referenceFactsErrors[intent] ??
                      trackedErrorFor(`grounding.${intent}.reference_facts`);
                    const parsedFacts = parseReferenceFacts(draft.referenceFactsJson);

                    return (
                      <details
                        key={intent}
                        ref={(node) => {
                          detailsRefs.current[intent] = node;
                        }}
                        open={openIntents.has(intent)}
                        onToggle={(event) => {
                          const isOpen = event.currentTarget.open;
                          setOpenIntents((prev) => {
                            const next = new Set(prev);
                            if (isOpen) next.add(intent);
                            else next.delete(intent);
                            return next;
                          });
                        }}
                        data-testid={`grounding-${intent}-details`}
                        className="rounded-lg bg-surface-2 ring-1 ring-inset ring-ink-3/10"
                      >
                        <summary className="cursor-pointer select-none px-4 py-3 text-meta font-medium uppercase tracking-wide text-ink-3">
                          {intent} ·{' '}
                          {t('config.edit.grounding.factCount', { count: draft.factKeys.length })}
                          {hasGroundingError(intent) ? (
                            <span aria-hidden="true" className="ml-2 text-status-down">
                              ⚠
                            </span>
                          ) : null}
                        </summary>

                        <div className="flex flex-col gap-4 px-4 pb-4">
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor={`grounding-${intent}-instruction`}
                              className="text-meta font-medium text-ink-2"
                            >
                              {t('config.edit.grounding.instruction')}
                            </label>
                            <p className="text-meta text-ink-3">
                              {t('config.edit.grounding.instructionHint')}
                            </p>
                            {split.preamble !== '' ? (
                              <details className="rounded-md bg-surface-1 px-3 py-2 ring-1 ring-inset ring-ink-3/10">
                                <summary className="cursor-pointer max-md:min-h-6 text-meta text-ink-3">
                                  {t('config.edit.grounding.sharedPreamble')}
                                </summary>
                                <p className="mt-1 text-meta text-ink-2">{split.preamble}</p>
                                <button
                                  type="button"
                                  data-testid={`grounding-${intent}-edit-shared-preamble`}
                                  onClick={() =>
                                    setForceFullInstruction((prev) => new Set(prev).add(intent))
                                  }
                                  className="mt-1 max-md:min-h-6 text-meta text-gold hover:underline"
                                >
                                  {t('config.edit.grounding.editSharedPreamble')}
                                </button>
                              </details>
                            ) : null}
                            <ExpandableTextarea
                              id={`grounding-${intent}-instruction`}
                              testId={`grounding-${intent}-instruction`}
                              value={split.remainder}
                              disabled={createPhase === 'submitting'}
                              minRows={4}
                              onChange={(nextRemainder) =>
                                setGrounding((prev) => ({
                                  ...prev,
                                  [intent]: {
                                    ...prev[intent]!,
                                    instruction: joinInstruction({
                                      preamble: split.preamble,
                                      remainder: nextRemainder,
                                    }),
                                  },
                                }))
                              }
                            />
                            {instructionError != null ? (
                              <p
                                role="alert"
                                data-testid={`grounding-${intent}-instruction-error`}
                                className="text-meta text-status-down"
                              >
                                {instructionError}
                              </p>
                            ) : null}
                          </div>

                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor={`grounding-${intent}-anchor`}
                              className="text-meta font-medium text-ink-2"
                            >
                              {t('config.edit.grounding.anchorTemplate')}
                            </label>
                            <p className="text-meta text-ink-3">
                              {t('config.edit.grounding.anchorTemplateHint')}
                            </p>
                            <ExpandableTextarea
                              id={`grounding-${intent}-anchor`}
                              testId={`grounding-${intent}-anchor`}
                              value={draft.anchorTemplate}
                              disabled={createPhase === 'submitting'}
                              minRows={2}
                              onChange={(next) =>
                                setGrounding((prev) => ({
                                  ...prev,
                                  [intent]: { ...prev[intent]!, anchorTemplate: next },
                                }))
                              }
                            />
                            {anchorError != null ? (
                              <p
                                role="alert"
                                data-testid={`grounding-${intent}-anchor-error`}
                                className="text-meta text-status-down"
                              >
                                {anchorError}
                              </p>
                            ) : null}
                          </div>

                          <div className="flex flex-col gap-2">
                            <p className="text-meta font-medium text-ink-2">
                              {t('config.edit.grounding.referenceFacts')}
                            </p>
                            <p className="text-meta text-ink-3">
                              {t('config.edit.grounding.referenceFactsHint')}
                            </p>
                            {factsError != null ? (
                              <p
                                role="alert"
                                data-testid={`grounding-${intent}-facts-error`}
                                className="text-meta text-status-down"
                              >
                                {factsError}
                              </p>
                            ) : null}

                            {draft.factKeys.length > 0 ? (
                              <div className="relative overflow-x-auto rounded-lg ring-1 ring-inset ring-ink-3/10">
                                <table className="w-full border-collapse text-left text-body">
                                  <thead className="bg-surface-1 text-meta uppercase tracking-wide text-ink-3">
                                    <tr>
                                      <th scope="col" className="px-3 py-2 font-medium">
                                        {t('config.edit.grounding.factsColumns.key')}
                                      </th>
                                      <th scope="col" className="px-3 py-2 font-medium">
                                        {t('config.edit.grounding.factsColumns.value')}
                                      </th>
                                      <th scope="col" className="px-3 py-2 font-medium">
                                        {t('config.edit.grounding.factCategoriesHeading')}
                                      </th>
                                      <th scope="col" className="px-3 py-2 font-medium">
                                        {t('config.edit.grounding.factsColumns.pattern')}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {draft.factKeys.map((key) => {
                                      const rawValue = parsedFacts[key];
                                      const isList = Array.isArray(rawValue);
                                      const value = rawValue ?? (isList ? [] : '');
                                      const category = draft.factCategories[key] ?? '';
                                      return (
                                        <tr key={key} className="border-t border-outline align-top">
                                          <th
                                            scope="row"
                                            className="px-3 py-2 font-mono font-normal text-ink-2"
                                          >
                                            {key}
                                          </th>
                                          <td className="px-3 py-2">
                                            {isList ? (
                                              <textarea
                                                data-testid={`grounding-${intent}-fact-${key}-value`}
                                                aria-label={key}
                                                rows={Math.max(2, (value as string[]).length)}
                                                value={(value as string[]).join('\n')}
                                                disabled={createPhase === 'submitting'}
                                                onChange={(event) =>
                                                  setFactValue(
                                                    intent,
                                                    key,
                                                    event.target.value === ''
                                                      ? []
                                                      : event.target.value.split('\n'),
                                                  )
                                                }
                                                className={`${inputClass} w-full font-mono`}
                                              />
                                            ) : (
                                              <input
                                                type="text"
                                                data-testid={`grounding-${intent}-fact-${key}-value`}
                                                aria-label={key}
                                                value={value as string}
                                                disabled={createPhase === 'submitting'}
                                                onChange={(event) =>
                                                  setFactValue(intent, key, event.target.value)
                                                }
                                                className={`${inputClass} w-full`}
                                              />
                                            )}
                                          </td>
                                          <td className="px-3 py-2">
                                            <Field
                                              id={`grounding-${intent}-fact-${key}-category`}
                                              label={key}
                                              labelHidden
                                              hint={
                                                category !== ''
                                                  ? t(
                                                      `config.edit.grounding.categoryHint.${category}`,
                                                    )
                                                  : undefined
                                              }
                                              error={trackedErrorFor(
                                                `grounding.${intent}.fact_categories.${key}`,
                                              )}
                                            >
                                              <select
                                                id={`grounding-${intent}-fact-${key}-category`}
                                                data-testid={`grounding-${intent}-fact-${key}-category`}
                                                value={category}
                                                disabled={createPhase === 'submitting'}
                                                onChange={(event) =>
                                                  setGrounding((prev) => ({
                                                    ...prev,
                                                    [intent]: {
                                                      ...prev[intent]!,
                                                      factCategories: {
                                                        ...prev[intent]!.factCategories,
                                                        [key]: event.target.value as
                                                          FactCategory | '',
                                                      },
                                                    },
                                                  }))
                                                }
                                                className={inputClass}
                                              >
                                                <option value="">
                                                  {t('config.edit.grounding.categoryUnset')}
                                                </option>
                                                {FACT_CATEGORIES.map((option) => (
                                                  <option key={option} value={option}>
                                                    {t(
                                                      `config.edit.grounding.categoryValues.${option}`,
                                                    )}
                                                  </option>
                                                ))}
                                              </select>
                                            </Field>
                                          </td>
                                          <td className="px-3 py-2 text-meta text-ink-3">
                                            {original?.mandatory_fact_patterns?.[key] != null ? (
                                              <span
                                                data-testid={`grounding-${intent}-fact-${key}-patterns`}
                                              >
                                                {t('config.edit.grounding.patternsHint', {
                                                  patterns:
                                                    original.mandatory_fact_patterns[key]!.join(
                                                      ' | ',
                                                    ),
                                                })}
                                              </span>
                                            ) : (
                                              <span aria-hidden="true">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}

                            <details
                              data-testid={`grounding-${intent}-facts-advanced`}
                              onToggle={(event) => {
                                if (event.currentTarget.open) return; // only re-derive on CLOSE
                                setGrounding((prev) => ({
                                  ...prev,
                                  [intent]: rederiveFactRows(prev[intent]!),
                                }));
                              }}
                              className="rounded-md bg-surface-1 px-3 py-2 ring-1 ring-inset ring-ink-3/10"
                            >
                              <summary className="cursor-pointer max-md:min-h-6 text-meta text-ink-3">
                                {t('config.edit.grounding.advancedJsonToggle')}
                              </summary>
                              <textarea
                                id={`grounding-${intent}-facts`}
                                data-testid={`grounding-${intent}-facts`}
                                rows={8}
                                value={draft.referenceFactsJson}
                                disabled={createPhase === 'submitting'}
                                onChange={(event) =>
                                  setGrounding((prev) => ({
                                    ...prev,
                                    [intent]: {
                                      ...prev[intent]!,
                                      referenceFactsJson: event.target.value,
                                    },
                                  }))
                                }
                                className={`${inputClass} mt-2 w-full font-mono`}
                              />
                            </details>
                          </div>

                          {original != null && original.mandatory_string_categories.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              <p className="text-meta text-ink-3">
                                {t('config.edit.grounding.mandatoryStringCategories')}:{' '}
                                {original.mandatory_string_categories.join(', ')}
                              </p>
                              <p className="text-meta text-ink-3">
                                {t('config.edit.grounding.mandatoryStringCategoriesHint')}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <section aria-labelledby="config-facts-heading" className="flex flex-col gap-3">
            <h3 id="config-facts-heading" className="text-h3 text-ink-1">
              {t('config.edit.productFacts.heading')}
            </h3>
            <p className="text-meta text-ink-3">{t('config.edit.productFacts.headingHint')}</p>
            {Object.entries(productFacts).map(([key, value]) => (
              <Field
                key={key}
                id={`fact-${key}-value`}
                label={key}
                error={trackedErrorFor(`product_facts.${key}`)}
              >
                <input
                  id={`fact-${key}-value`}
                  data-testid={`fact-${key}-value`}
                  type="text"
                  value={value}
                  disabled={createPhase === 'submitting'}
                  onChange={(event) =>
                    setProductFacts((prev) => ({ ...prev, [key]: event.target.value }))
                  }
                  className={inputClass}
                />
              </Field>
            ))}
            {Object.keys(productFacts).length === 0 ? (
              <p data-testid="config-facts-empty" className="text-body text-ink-2">
                {t('config.edit.productFacts.empty')}
              </p>
            ) : null}
          </section>

          <section aria-labelledby="config-classifiers-heading" className="flex flex-col gap-3">
            <h3 id="config-classifiers-heading" className="text-h3 text-ink-1">
              {t('config.edit.classifiers.heading')}
            </h3>
            <p className="text-meta text-ink-3">{t('config.edit.classifiers.headingHint')}</p>
            {Object.entries(classifiers).map(([key, config]) => (
              <div key={key} className="flex flex-col gap-1">
                <label
                  htmlFor={`classifier-${key}-system-prompt`}
                  className="text-meta font-medium text-ink-2"
                >
                  {key}
                </label>
                <ExpandableTextarea
                  id={`classifier-${key}-system-prompt`}
                  testId={`classifier-${key}-system-prompt`}
                  value={config.system_prompt}
                  disabled={createPhase === 'submitting'}
                  minRows={4}
                  onChange={(next) =>
                    setClassifiers((prev) => ({
                      ...prev,
                      [key]: { ...prev[key]!, system_prompt: next },
                    }))
                  }
                />
                {(() => {
                  const error = trackedErrorFor(`classifiers.${key}.system_prompt`);
                  return error != null ? (
                    <p
                      role="alert"
                      data-testid={`classifier-${key}-system-prompt-error`}
                      className="text-meta text-status-down"
                    >
                      {error}
                    </p>
                  ) : null;
                })()}
              </div>
            ))}
            {Object.keys(classifiers).length === 0 ? (
              <p data-testid="config-classifiers-empty" className="text-body text-ink-2">
                {t('config.edit.classifiers.empty')}
              </p>
            ) : null}
          </section>

          {(() => {
            // Evaluated HERE, not as an earlier `const` — see the note by
            // `trackedErrorFor` above. By this point every field above has
            // already run its `trackedErrorFor` call, so `consumedPaths` is final.
            const unmatchedErrors = fieldErrors.filter(
              (error) => !consumedPaths.has(error.field_path),
            );
            if (unmatchedErrors.length === 0) return null;
            return (
              <div
                role="alert"
                data-testid="config-unmatched-errors"
                className="min-w-0 break-words rounded-lg bg-status-down/15 p-3 text-meta text-status-down ring-1 ring-inset ring-status-down/40 [overflow-wrap:anywhere]"
              >
                <p className="font-medium">{t('config.edit.errors.otherFields')}</p>
                <ul className="list-inside list-disc">
                  {unmatchedErrors.map((error) => (
                    <li key={error.field_path}>
                      {error.field_path}: {error.reason}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Sticky bottom action bar (§7) — Create/Activate reachable from
          anywhere on what can be a very long page. */}
          <div className="sticky bottom-0 z-10 flex flex-col gap-3 rounded-t-lg border-t border-ink-3/20 bg-surface-1 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.25)] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1">
              <button
                type="button"
                data-testid="config-create-submit"
                disabled={createPhase === 'submitting' || newConfigVersion.trim() === ''}
                aria-busy={createPhase === 'submitting'}
                onClick={() => floating(handleCreate())}
                className="inline-flex w-fit items-center rounded-lg bg-gold max-md:min-h-11 px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90 disabled:opacity-50"
              >
                {createPhase === 'submitting' ? t('config.edit.creating') : t('config.edit.create')}
              </button>
              {/* Saving alone changes nothing — stated plainly regardless of outcome. */}
              <p className="text-meta text-ink-3">{t('config.edit.saveOnlyHint')}</p>
            </div>

            {createPhase === 'created' && created != null ? (
              <div className="flex items-center gap-3">
                {created.warnings.length > 0 ? (
                  <a
                    href="#config-create-warnings"
                    data-testid="config-warnings-badge"
                    className="rounded-full bg-warning/15 max-md:inline-flex max-md:min-h-6 max-md:items-center px-2 py-0.5 text-meta font-medium text-warning ring-1 ring-inset ring-warning/40 hover:underline"
                  >
                    {t('config.edit.grounding.warningsBadge', { count: created.warnings.length })}
                  </a>
                ) : null}
                <ActivateVersionControl
                  apiClient={apiClient}
                  skillName={base.skillName}
                  configVersion={created.config_version}
                  productCode={base.productCode}
                  isActive={false}
                  onActivated={onActivated}
                />
              </div>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
};
